// QA sampler redactor slice — admin spot-check queue UI.
//
// Two-pane layout: list of `pending_admin_review` snapshots on the left,
// detail + edit on the right. Each snapshot row shows mode, organisation,
// captured-at age, confidence score, and any redactor-emitted flags as
// chips. Clicking a row opens the detail pane with the raw transcript +
// redactor draft side-by-side; the admin edits the draft in a textarea
// and clicks Approve to promote (writes to `qa_rubric_samples`) or
// Reject to dismiss (snapshot status flips to `rejected`; the snapshot
// row stays for the 90-day TTL so the audit trail and Article-17 trace
// remain intact).
//
// No auto-promotion regardless of redactor confidence: every promotion
// is a click, every promotion's `createdByAdminId` is the spot-checking
// admin. Failure-closed posture for the redactor means low-confidence
// or failed rows surface here with the regex pre-pass as the draft and
// a `redactor_failed:<reason>` flag chip.

import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApproveRedactionSchema,
  type ApproveRedactionBody,
  type PendingRedactionDetail,
  type PendingRedactionListResponse,
  type PendingRedactionSummary,
} from '@besideu/shared/qa-rubric';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';

export function AdminQaRedaction() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['admin', 'qa-pending-redaction'],
    queryFn: () => api.get<PendingRedactionListResponse>('/api/admin/qa-rubric/pending-redaction'),
    refetchInterval: 60_000,
  });

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">QA redaction spot-check</h1>
        <p className="text-sm text-muted-foreground">
          Review and approve auto-redacted Companion session snapshots before they reach the CGC
          scoring pool. The redactor runs on the daily cron; every promotion is an explicit click.{' '}
          <Link to="/admin/governance" className="text-primary underline-offset-2 hover:underline">
            Back to Governance
          </Link>
        </p>
      </header>

      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading queue…</p>
      ) : list.error || !list.data ? (
        <p className="text-sm text-traffic-red" role="alert">
          Could not load queue.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[24rem_1fr]">
          <QueueList
            data={list.data}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId(id)}
          />
          <DetailPane
            snapshotId={selectedId}
            onDone={() => {
              setSelectedId(null);
            }}
          />
        </div>
      )}
    </section>
  );
}

function QueueList({
  data,
  selectedId,
  onSelect,
}: {
  data: PendingRedactionListResponse;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
        <p>
          <span className="font-medium">{data.counts.pendingAdminReview}</span> awaiting review ·{' '}
          <span className="text-muted-foreground">
            {data.counts.pendingRedaction} awaiting redactor
          </span>
        </p>
        {!data.redactorPromptApproved ? (
          <p className="mt-2 text-muted-foreground">
            <span className="font-mono">QA_Redactor</span> prompt is not approved — every queued row
            carries the regex-only draft + a <code>redactor_failed:prompt_not_found</code> flag.
          </p>
        ) : null}
      </div>

      {data.snapshots.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Nothing in review right now. The next cron tick will pick up any{' '}
          <span className="font-mono">pending_redaction</span> rows.
        </p>
      ) : (
        <ul className="space-y-2">
          {data.snapshots.map((snapshot) => (
            <li key={snapshot.id}>
              <QueueRow
                snapshot={snapshot}
                isSelected={selectedId === snapshot.id}
                onSelect={() => onSelect(snapshot.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueRow({
  snapshot,
  isSelected,
  onSelect,
}: {
  snapshot: PendingRedactionSummary;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const captured = new Date(snapshot.capturedAt).toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'block w-full rounded-md border p-3 text-left text-sm transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border bg-background hover:bg-muted/40',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">{snapshot.organizationName}</p>
          <p className="text-xs text-muted-foreground">{snapshot.mode}</p>
        </div>
        <ConfidenceBadge confidence={snapshot.redactionConfidence} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{captured}</p>
      {snapshot.redactionFlags.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-1">
          {snapshot.redactionFlags.map((flag) => (
            <li
              key={flag}
              className="rounded bg-traffic-amber/10 px-1.5 py-0.5 font-mono text-[10px] text-traffic-amber-text"
            >
              {flag}
            </li>
          ))}
        </ul>
      ) : null}
    </button>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number | null }) {
  if (confidence === null) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
        pending
      </span>
    );
  }
  const pct = Math.round(confidence * 100);
  // Tier the colour so the operator gets a coarse visual signal: < 50% =
  // red, < 80% = amber, else green. The exact thresholds are not
  // load-bearing — they're operator hints, not a gate.
  const tone =
    confidence < 0.5
      ? 'bg-traffic-red/10 text-traffic-red'
      : confidence < 0.8
        ? 'bg-traffic-amber/10 text-traffic-amber-text'
        : 'bg-traffic-green/10 text-traffic-green-text';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>{pct}%</span>;
}

function DetailPane({ snapshotId, onDone }: { snapshotId: string | null; onDone: () => void }) {
  if (snapshotId === null) {
    return (
      <p className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
        Select a snapshot from the queue to review.
      </p>
    );
  }
  return <DetailPaneForSnapshot key={snapshotId} snapshotId={snapshotId} onDone={onDone} />;
}

function DetailPaneForSnapshot({ snapshotId, onDone }: { snapshotId: string; onDone: () => void }) {
  const detail = useQuery({
    queryKey: ['admin', 'qa-pending-redaction', snapshotId],
    queryFn: () =>
      api.get<{ snapshot: PendingRedactionDetail }>(
        `/api/admin/qa-rubric/pending-redaction/${snapshotId}`,
      ),
    // No retry on 404 (snapshot may have just been approved/rejected by
    // a different tab) — fail fast and let the operator pick a fresh row.
    retry: false,
  });

  if (detail.isPending) {
    return (
      <p className="rounded-md border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
        Loading…
      </p>
    );
  }
  if (detail.isError) {
    return (
      <p className="rounded-md border border-border bg-muted/20 p-6 text-sm text-traffic-red">
        This snapshot is no longer available (it may have been approved or rejected in another tab).{' '}
        <button type="button" onClick={onDone} className="underline">
          Clear selection
        </button>
      </p>
    );
  }

  return <DetailEditor snapshot={detail.data.snapshot} onDone={onDone} />;
}

function DetailEditor({
  snapshot,
  onDone,
}: {
  snapshot: PendingRedactionDetail;
  onDone: () => void;
}) {
  // Initialise the editor with the redactor's draft when present;
  // otherwise the raw transcript so the admin can hand-redact from
  // scratch (the failure-closed path).
  const [draft, setDraft] = useState(snapshot.redactedDraft ?? snapshot.rawTranscript);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const queryClient = useQueryClient();

  const approve = useMutation({
    mutationFn: (body: ApproveRedactionBody) =>
      api.post<{ ok: true; sampleId: string }>(
        `/api/admin/qa-rubric/pending-redaction/${snapshot.id}/approve`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-pending-redaction'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-samples'] });
      onDone();
    },
  });

  const reject = useMutation({
    mutationFn: (body: { reason: string }) =>
      api.post<{ ok: true }>(`/api/admin/qa-rubric/pending-redaction/${snapshot.id}/reject`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-pending-redaction'] });
      onDone();
    },
  });

  function submitApprove() {
    const parsed = ApproveRedactionSchema.safeParse({ redactedTranscript: draft });
    if (!parsed.success) return;
    approve.mutate(parsed.data);
  }

  function submitReject() {
    const trimmed = rejectReason.trim();
    if (trimmed.length === 0) return;
    reject.mutate({ reason: trimmed });
  }

  return (
    <div className="space-y-4 rounded-md border border-border bg-background p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="font-medium">{snapshot.organizationName}</p>
          <p className="text-xs text-muted-foreground">
            {snapshot.mode} · captured{' '}
            {new Date(snapshot.capturedAt).toLocaleString('en-GB', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
            {' · TTL '}
            {new Date(snapshot.scheduledForDeletionAt).toLocaleDateString('en-GB')}
          </p>
        </div>
        <ConfidenceBadge confidence={snapshot.redactionConfidence} />
      </header>

      {snapshot.redactionFlags.length > 0 ? (
        <ul className="flex flex-wrap gap-1">
          {snapshot.redactionFlags.map((flag) => (
            <li
              key={flag}
              className="rounded bg-traffic-amber/10 px-1.5 py-0.5 font-mono text-[10px] text-traffic-amber-text"
            >
              {flag}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Raw transcript
          </p>
          <pre className="mt-1 max-h-96 overflow-auto rounded-md border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
            {snapshot.rawTranscript}
          </pre>
        </div>
        <div>
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Redacted (editable)
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="mt-1 block h-96 w-full resize-y rounded-md border border-border bg-background p-3 font-mono text-xs"
            maxLength={50_000}
          />
          <p className="mt-1 text-[10px] text-muted-foreground">
            {draft.length} characters · max 50,000
          </p>
        </div>
      </div>

      {approve.isError ? (
        <p className="text-sm text-traffic-red" role="alert">
          Couldn't approve.{' '}
          {approve.error instanceof ApiError && approve.error.message
            ? approve.error.message
            : 'Please try again.'}
        </p>
      ) : null}
      {reject.isError ? (
        <p className="text-sm text-traffic-red" role="alert">
          Couldn't reject.{' '}
          {reject.error instanceof ApiError && reject.error.message
            ? reject.error.message
            : 'Please try again.'}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {showReject ? (
          <RejectForm
            reason={rejectReason}
            onReasonChange={setRejectReason}
            onCancel={() => {
              setShowReject(false);
              setRejectReason('');
            }}
            onSubmit={submitReject}
            loading={reject.isPending}
          />
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => setShowReject(true)}
              className="py-2 text-sm"
              disabled={approve.isPending}
            >
              Reject…
            </Button>
            <Button
              onClick={submitApprove}
              loading={approve.isPending}
              disabled={draft.trim().length === 0}
              className="py-2 text-sm"
            >
              Approve &amp; promote to CGC
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function RejectForm({
  reason,
  onReasonChange,
  onCancel,
  onSubmit,
  loading,
}: {
  reason: string;
  onReasonChange: (s: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  return (
    <div className="w-full space-y-2">
      <label className="block">
        <span className="text-xs font-medium text-muted-foreground">Reason (audit log entry)</span>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          rows={3}
          maxLength={2_000}
          placeholder="e.g. transcript contains identifiable medical detail the redactor missed"
          className="mt-1 block w-full resize-y rounded-md border border-border bg-background p-2 text-sm"
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} className="py-2 text-sm" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          loading={loading}
          disabled={reason.trim().length === 0}
          className="py-2 text-sm"
        >
          Confirm reject
        </Button>
      </div>
    </div>
  );
}
